import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getUserRole } from '../api';

/**
 * DemoModeContext
 *
 * View mode context for switching between teacher and student shells.
 * Backward-compatible exports are preserved to minimize refactor churn.
 * Uses sessionStorage so demo mode resets on browser close.
 */

const STORAGE_KEY = 'lrbrain-app-view';

const normalizeRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'brainiac') return 'student';
  return normalized;
};

const DemoModeContext = createContext(null);

export const DemoModeProvider = ({ children }) => {
  // Get actual role from localStorage (set during OAuth).
  const actualRole = normalizeRole(getUserRole());
  const isActualAdmin = actualRole === 'admin';
  const canSwitchView = actualRole === 'admin' || actualRole === 'team_captain';

  // Initialize view from sessionStorage (resets on browser close).
  const [currentView, setCurrentView] = useState(() => {
    if (!canSwitchView) return 'student';
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored === 'student' || stored === 'teacher' ? stored : 'teacher';
    } catch {
      return 'teacher';
    }
  });

  // Keep view valid as role changes.
  useEffect(() => {
    if (!canSwitchView && currentView !== 'student') {
      setCurrentView('student');
    }
  }, [canSwitchView, currentView]);

  // Persist to sessionStorage.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, currentView);
    } catch {
      // sessionStorage not available
    }
  }, [currentView]);

  const setView = useCallback((nextView) => {
    if (!canSwitchView) return;
    const normalized = nextView === 'student' ? 'student' : 'teacher';
    setCurrentView(normalized);
  }, [canSwitchView]);

  // Backward compatibility helper names.
  const toggleDemoMode = useCallback(() => {
    if (!canSwitchView) return;
    setCurrentView(prev => (prev === 'student' ? 'teacher' : 'student'));
  }, [canSwitchView]);

  const enableDemoMode = useCallback(() => {
    if (!canSwitchView) return;
    setCurrentView('student');
  }, [canSwitchView]);

  const disableDemoMode = useCallback(() => {
    if (!canSwitchView) return;
    setCurrentView('teacher');
  }, [canSwitchView]);

  const isStudentView = currentView === 'student';
  const isTeacherView = currentView === 'teacher';
  const isDemoMode = canSwitchView && isStudentView;

  // No role spoofing anymore.
  const getEffectiveRole = useCallback(() => {
    return actualRole;
  }, [actualRole]);

  const getCurrentView = useCallback(() => {
    if (!canSwitchView) return 'student';
    return currentView;
  }, [canSwitchView, currentView]);

  // Check if user is effectively admin
  const isEffectiveAdmin = useCallback(() => {
    return getEffectiveRole() === 'admin';
  }, [getEffectiveRole]);

  const value = {
    // State
    isDemoMode, // legacy alias: true when privileged user is in student view
    isActualAdmin,
    actualRole,
    currentView,
    isStudentView,
    isTeacherView,
    canSwitchView,

    // Actions
    toggleDemoMode,
    enableDemoMode,
    disableDemoMode,
    setView,

    // Role helpers
    getEffectiveRole,
    getCurrentView,
    isEffectiveAdmin
  };

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = () => {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
};

export default DemoModeContext;
